import React, { useEffect, useState } from 'react';
import { getAdminReports } from './adminApi';
import './Reports.css';

const iso = (date) => [date.getFullYear(), String(date.getMonth()+1).padStart(2,'0'), String(date.getDate()).padStart(2,'0')].join('-');
const daysAgo = (days) => { const d = new Date(); d.setDate(d.getDate()-days); return iso(d); };
const today = () => iso(new Date());
const presets = {
  today: () => [today(), today()],
  yesterday: () => [daysAgo(1), daysAgo(1)],
  week: () => [daysAgo(6), today()],
  month30: () => [daysAgo(29), today()],
  thisMonth: () => { const d = new Date(); return [iso(new Date(d.getFullYear(), d.getMonth(), 1)), today()]; },
};
const sections = [
  { id: 'orders', title: 'Order Report', fields: [['order_number','Order'],['date','Date'],['table','Table'],['status','Order Status'],['payment_status','Payment Status'],['payment_method','Method'],['total','Total']] },
  { id: 'payments', title: 'Payment Breakdown', fields: [['payment_method','Method'],['orders','Paid Orders'],['amount','Amount']] },
  { id: 'items', title: 'Item Sales', fields: [['item_name','Item'],['quantity','Quantity'],['revenue','Revenue']] },
  { id: 'categories', title: 'Category Sales', fields: [['category','Category'],['quantity','Quantity'],['revenue','Revenue']] },
];
const csvCell = (v) => '"' + String(v ?? '').replace(/^[=+\-@\t\r]/, "'$&").replace(/"/g,'""') + '"';
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export default function Reports({ restaurantId }) {
  const [start, setStart] = useState(daysAgo(6));
  const [end, setEnd] = useState(today());
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('orders');
  const [stale, setStale] = useState(false);
  async function load(s = start, e = end) {
    if (s > e || e > today()) { setError('Select a valid date range, ending no later than today.'); return; }
    setLoading(true); setError('');
    try { setReport(await getAdminReports(restaurantId, s, e)); setStale(false); }
    catch (err) { setError(err.message); setReport(null); }
    finally { setLoading(false); }
  }
  useEffect(() => {
    let active = true;
    setLoading(true); setReport(null); setError('');
    getAdminReports(restaurantId, daysAgo(6), today()).then(r => { if(active) setReport(r); })
      .catch(e => { if(active) setError(e.message); }).finally(() => { if(active) setLoading(false); });
    return () => { active = false; };
  }, [restaurantId]);
  function preset(key) { const [s,e] = presets[key](); setStart(s); setEnd(e); load(s,e); }
  function downloadCSV() {
    if (!report || stale) return;
    const section = sections.find(s => s.id === tab);
    const rows = [section.fields.map(f => csvCell(f[1])).join(',')];
    (report[tab] || []).forEach(row => rows.push(section.fields.map(f => csvCell(row[f[0]])).join(',')));
    downloadBlob(new Blob(['\uFEFF'+rows.join('\r\n')], {type:'text/csv;charset=utf-8'}), `${tab}_${report.start_date}_${report.end_date}.csv`);
  }
  async function downloadPDF() {
    if (!report || stale) return;
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const section = sections.find(s => s.id === tab);
      let y = 15;
      const pageWidth = doc.internal.pageSize.getWidth();
      const line = (text, bold=false) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        const wrapped = doc.splitTextToSize(String(text), pageWidth - 24);
        for (const part of wrapped) { if (y > 275) { doc.addPage(); y=15; } doc.text(part, 12, y); y+=6; }
      };
      line(report.restaurant, true); line(`${section.title} | ${report.start_date} to ${report.end_date}`, true);
      line(`Revenue: ${report.currency} ${report.summary.revenue} | Paid orders: ${report.summary.paid_orders}`);
      y+=3;
      (report[tab] || []).forEach((row, i) => {
        const text = section.fields.map(([key,label]) => `${label}: ${row[key] ?? ''}`).join('  |  ');
        line(`${i+1}. ${text}`); y+=2;
      });
      doc.save(`${tab}_${report.start_date}_${report.end_date}.pdf`);
    } catch(e) { setError(`PDF download failed: ${e.message}`); }
  }
  const money = (value) => `${report?.currency || 'INR'} ${Number(value || 0).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const section = sections.find(s => s.id === tab);
  return <section className="rr-page">
    <div className="rr-top"><div><h2>Reports</h2><p>Filter sales and orders, print or download a report.</p></div></div>
    <div className="rr-controls rr-no-print">
      <div className="rr-presets">{[['today','Today'],['yesterday','Yesterday'],['week','Last 7 Days'],['month30','Last 30 Days'],['thisMonth','This Month']].map(([key,label])=><button key={key} type="button" onClick={()=>preset(key)}>{label}</button>)}</div>
      <div className="rr-dates"><label>From <input type="date" value={start} max={today()} onChange={e=>{setStart(e.target.value);setStale(true);}} /></label><label>To <input type="date" value={end} max={today()} onChange={e=>{setEnd(e.target.value);setStale(true);}} /></label><button type="button" disabled={loading || !start || !end} onClick={()=>load()}>{loading?'Loading...':'Apply Filter'}</button></div>
    </div>
    {error && <p className="rr-error" role="alert">{error}</p>}
    {loading && <p>Loading report...</p>}
    {report && <>
      <div className="rr-print-head"><h2>{report.restaurant}</h2><p>Sales Report · {report.start_date} to {report.end_date}</p></div>
      {stale && <p className="rr-warning rr-no-print">Dates changed. Click Apply Filter before exporting.</p>}
      <div className="rr-stats">{[['Revenue',money(report.summary.revenue)],['Paid Orders',report.summary.paid_orders],['Total Orders',report.summary.total_orders],['Average Order Value',money(report.summary.average_order_value)],['Items Sold',report.summary.items_sold]].map(([label,value])=><div key={label}><span>{label}</span><strong>{value}</strong></div>)}</div>
      <div className="rr-tabs rr-no-print">{sections.map(s=><button type="button" key={s.id} className={tab===s.id?'selected':''} onClick={()=>setTab(s.id)}>{s.title}</button>)}</div>
      <div className="rr-actions rr-no-print"><button disabled={stale || loading} onClick={()=>window.print()}>Print</button><button disabled={stale || loading} onClick={downloadPDF}>Download PDF</button><button disabled={stale || loading} onClick={downloadCSV}>Download CSV</button></div>
      <h3>{section.title}</h3>
      <div className="rr-scroll"><table className="rr-table"><thead><tr>{section.fields.map(([key,label])=><th key={key}>{label}</th>)}</tr></thead><tbody>{(report[tab] || []).length ? report[tab].map((row,i)=><tr key={i}>{section.fields.map(([key])=><td key={key}>{row[key] ?? '—'}</td>)}</tr>) : <tr><td colSpan={section.fields.length}>No records in this period.</td></tr>}</tbody></table></div>
      <p className="rr-note">{report.notes.join(' ')}</p>
    </>}
  </section>;
}
